import * as React from 'react';
import Modal from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Tooltip from 'antd/lib/tooltip';
import Spin from 'antd/lib/spin';
import withStyles from 'react-jss';
import { useGenericState } from 'src/Library/UseGenericState';
import { commonStyles } from 'src/Library/CommonStyles';
import { StylesProps } from 'src/Library/Types';
import { nl2Br } from 'src/utils/DOMUtils';
import { tokens } from 'src/design-system';

const styles = (theme) => ({
  ellipses: {
  ...commonStyles(theme).ellipses,
  },
  rejectInquiryModal: {
    '& .ant-modal-title': {
      fontSize: `${tokens.fontSize.h3}px`,
      color: '#444444',
      fontWeight: 'bold',
    },
    '& .ant-modal-body': {
      padding: 0,
    },
  },
  remarkUnavailable: {
    color: '#B2B2B2',
    fontSize: `${tokens.fontSize.small}px`,
  },
  seeMoreText: {
    verticalAlign: 'top', 
    color: '#1478F6', 
    marginLeft: '2px'
  },
  textboxDiv: {
    '& div:last-child': {
      display: 'inline'
    }
  }
});

interface FFData {
  id: number;
  name: string;
  rejectionReasons: string[];
  remarks: string;
}

interface IProps extends
  StylesProps<ReturnType<typeof styles>>{
  visible: boolean;
  handleCancel: () => void;
  ffRejectedData: FFData[];
  loading?: boolean;
}

interface StateProps {
  reasonShowMoreList: boolean[],
  remarkShowMoreList: boolean[]
}

const InquiryRejectedFFModal = (props: IProps) => {
  const { visible, handleCancel, classes, ffRejectedData, loading } = props;
  const [state, setState] = useGenericState<StateProps>({
    reasonShowMoreList: [],
    remarkShowMoreList: [],
  });

  React.useEffect(() => {
    setState({
      reasonShowMoreList: ffRejectedData?.map((_) => true),
      remarkShowMoreList: ffRejectedData?.map((_) => true),
    })
  }, [ffRejectedData]);

  const checkTrunctated = (text) => {
    const textTruncateLengthRemark = 54;
    const linesCount = text?.split('\n').length;
    return linesCount > 1 || textTruncateLengthRemark < text.length;
  };

  const getTruncatedText = (text) => {
    let newText = text?.split('\n')[0].trim();
    if (checkTrunctated(text)) {
      return newText.substring(0, 54) + "..";
    }
    return newText;
  }

  const checkReasonTextTruncated = (reasonList) => {
    return reasonList.length > 1;
  };

  const handleShowMore = (type, index, ShowMoreVal) => {
    if (type === 'reason') {
      setState({ reasonShowMoreList: { ...state.reasonShowMoreList, [index]: ShowMoreVal } });
    } else {
      setState({ remarkShowMoreList: { ...state.remarkShowMoreList, [index]: ShowMoreVal } });
    }
  };
  const renderRejectedFFs = () => {
    return (
      <div
        style={{
          padding: '15px 28px',
          fontSize: `${tokens.fontSize.body}px`,
          color: '#262626',
        }}
      >
        <Row gutter={24} style={{ padding: '15px 0px', margin: 0, fontWeight: 600 }}>
          <Col span={2}>S No.</Col>
          <Col span={4}>FF</Col>
          <Col span={8}>Reason</Col>
          <Col span={10}>Remark</Col>
        </Row>
        {ffRejectedData?.map((ffData, index) => {
          return (
            <Row
              gutter={24}
              style={{ padding: '10px 0px', margin: 0 }}
              key={ffData.id}
            >
              <Col span={2} style={{ paddingLeft: '25px' }}>
                {index + 1}
              </Col>
              <Col span={4}>
                <Tooltip title={ffData.name}>
                  <div className={classes.ellipses}>
                  {ffData.name}
                  </div>
                </Tooltip>
              </Col>
              <Col span={8}>
                {state.reasonShowMoreList[index] ? (
                  <>
                    <div  className={classes.ellipses} style={{ maxWidth: '38ch',display: 'inline-block', }}>
                      {ffData.rejectionReasons.length > 1 ? '-': ''} {" "}{ffData.rejectionReasons[0]}
                    </div>
                    {checkReasonTextTruncated(ffData.rejectionReasons) && (
                      <a
                        className={classes.seeMoreText}
                        onClick={() => handleShowMore('reason', index, false)}
                      >
                        See More
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    {ffData.rejectionReasons.map((reason, index) => {
                      return (
                        <div
                          style={
                            ffData.rejectionReasons.length - 1 === index
                              ? { display: 'inline-block' }
                              : {}
                          }
                        >
                          {ffData.rejectionReasons.length > 1 ? '-': ''} {" "}{reason}
                        </div>
                      );
                    })}
                    <a
                      className={classes.seeMoreText}
                      onClick={() => handleShowMore('reason', index, true)}
                    >
                      See Less
                    </a>
                  </>
                )}
              </Col>
              <Col span={10}>
                {state.remarkShowMoreList[index] ? (
                  <>
                    <div
                      className={!ffData.remarks ? classes.remarkUnavailable: undefined}
                      style={{ display: 'inline-block'}}
                    >
                      {getTruncatedText(ffData.remarks || 'Not Available')}
                    </div>
                    {ffData.remarks && checkTrunctated(ffData.remarks) && (
                      <a
                        className={classes.seeMoreText}
                        onClick={() => handleShowMore('remark', index, false)}
                      >
                        See More
                      </a>
                    )}
                  </>
                ) : (
                  <>
                    <span className={classes.textboxDiv}>{nl2Br(ffData.remarks, '\n')}</span>
                    <a
                      className={classes.seeMoreText}
                      onClick={() => handleShowMore('remark', index, true)}
                    >
                      See Less
                    </a>
                  </>
                )}
              </Col>
            </Row>
          );
        })}
      </div>
    );
  };

  return (
    <Modal
      className={classes.rejectInquiryModal}
      width={1200}
      title="FFs Declined this Inquiry"
      visible={visible}
      style={{ padding: '0' }}
      onCancel={handleCancel}
      footer={null}
    >
      {loading? <Spin style={{ paddingLeft: '50%', paddingTop: '20px' }} /> : renderRejectedFFs()}
    </Modal>
  );
};

export default withStyles(styles)(React.memo(InquiryRejectedFFModal));
